import { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from './redux';
import { apiClient } from '../utils/apiClient';
import { logoutUser } from '../store/slices/authSlice';

interface DashboardData {
  dailySummary: {
    date: string;
    orders: {
      total: number;
      completed: number;
      pending: number;
      in_progress: number;
    };
    inventory: {
      stock_in: number;
      stock_out: number;
      current_total: number;
    };
    workers: Array<{
      worker_id: number;
      worker_name: string;
      items_picked: number;
    }>;
  };
  inventoryStatus: {
    total_styles: number;
    total_stock: number;
    low_stock_alerts: Array<{
      sku: string;
      style_code: string;
      size: string;
      color: string;
      current_quantity: number;
      threshold: number;
    }>;
    bin_utilization: number;
    rack_utilization: number;
  };
  userStats: {
    total: number;
    active: number;
    inactive: number;
    deleted: number;
    by_role: Record<string, number>;
    active_by_role: Record<string, number>;
  };
}

export const useDashboardData = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { isAuthenticated, token, user } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();

  const fetchDashboardData = async () => {
    if (!isAuthenticated || !token || !user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch dashboard data from real API endpoints
      const [dailySummaryResponse, inventoryStatusResponse, userStatsResponse] = await Promise.all([
        apiClient.getDailySummary(),
        apiClient.getInventoryStatus(),
        user.role === 'manager' ? apiClient.getUserStats() : Promise.resolve(null)
      ]);

      const dashboardData: DashboardData = {
        dailySummary: dailySummaryResponse.data,
        inventoryStatus: inventoryStatusResponse.data,
        userStats: userStatsResponse?.data || {
          total: 0,
          active: 0,
          inactive: 0,
          deleted: 0,
          by_role: {},
          active_by_role: {}
        }
      };

      setData(dashboardData);
    } catch (err: any) {
      console.error('Failed to fetch dashboard data:', err);
      
      // Handle authentication errors
      if (err.response?.status === 401) {
        console.log('Authentication failed, logging out user');
        dispatch(logoutUser());
        setError('Your session has expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const refetch = () => {
    fetchDashboardData();
  };

  useEffect(() => {
    fetchDashboardData();
  }, [isAuthenticated, token, user]);

  return {
    data,
    loading,
    error,
    refetch
  };
};
